import { tt } from './i18n/translation-memory'

/**
 * Which wallets a signed-in person may be shown - `FRONTEND.md` §9.661, FB-989.
 *
 * **The owner**: *"in Prehľady there is like error -> Peňaženky zamestnancov sa nepodarilo načítať... Can you make
 * it... that you will always see your own peňaženka?"*
 *
 * **Why the screen has to ask before it reads.** `/summaries` is guarded on the **permission** `summaries`, so a
 * franchise can grant it to anybody, while `GET /employees/wallets` is `@RequireAuth({"main_admin", "admin"})` - a
 * **role**. So a waiter granted *Prehľady* got a 403 and the screen reported it as *the wallets could not be
 * loaded, so we do not know whether the whole day is attributed*: a fabricated doubt about the day's money, which
 * is the exact thing FB-818's banner exists to prevent.
 *
 * **Read off the endpoint's own guard** rather than invented here, so the screen asks the question the server will
 * answer. A 403 that is never provoked needs no sentence, which is better than a better sentence.
 */
export type WalletScope = 'everyone' | 'self' | 'none'

/**
 * The two roles `GET /employees/wallets` admits, and what everybody else gets instead.
 *
 * **`self` for a waiter and a driver, because the server already allows exactly that**:
 * `GET /employees/{id}/waiter-wallet` and `/{id}/driver-wallet` admit those roles and
 * `WalletService` refuses somebody else's by id. So the right exists and what was missing was a screen using it.
 *
 * **`none` for a role that handles no money.** A kitchen role has no wallet, and an empty one would read as *you
 * took nothing today* rather than *this is not about you*.
 */
export function walletsScope(systemRole: string | null | undefined, alsoDrives: boolean): WalletScope {
  const role = (systemRole ?? '').trim()
  if (role === 'main_admin' || role === 'admin') return 'everyone'
  if (role === 'waiter' || role === 'driver') return 'self'
  // §9.686, owner #25 - an account the main admin marked *Jazdí aj rozvoz* carries delivery cash, so it has a
  // wallet whatever its role; `driver-wallet` admits it since §10A.344 and keeps it to its own.
  if (alsoDrives) return 'self'
  return 'none'
}

/**
 * Which of the two per-person reads this role's own wallet comes from, or null when there is none.
 *
 * **`alsoDrives` is required rather than optional**, §9.480's rule: an optional flag is dropped at a call site with
 * nothing to say so, which is how a marked cook would go on being told their role takes no money.
 */
export function ownWalletKind(
  systemRole: string | null | undefined,
  alsoDrives: boolean,
): 'waiter' | 'driver' | null {
  const role = (systemRole ?? '').trim()
  if (role === 'waiter') return 'waiter'
  if (role === 'driver') return 'driver'
  // §9.686 - a marked account that is neither, and not an administrator who reads everybody's: its money is what it
  // delivered, so its own read is the driver's. A marked waiter keeps the waiter read, which answers both columns
  // for a marked account since §10A.345.
  if (alsoDrives && role !== 'main_admin' && role !== 'admin') return 'driver'
  return null
}

/**
 * What to say to somebody whose role has no wallet.
 *
 * Says what the screen is about rather than that something is missing - a cook reading *your wallet could not be
 * loaded* would go looking for a fault.
 */
export function noWalletNote(): string {
  return tt('Peňaženku majú čašníci a šoféri - vaša rola nepreberá peniaze od zákazníkov.')
}
